import { popup as popupManager } from '@mc-alberta/tyrion'
import { lumber } from '@mc-alberta/lumber'
import { ACTIONS } from '../../../constants'
import getSrciUrl from '../../../utils/getSrciUrl'
import state from '../../../state'
import { abortCheckout, completeCheckout } from '../endSession'
import routines from './routines'
import getLocalStorageItem from '../../../utils/getLocalStorageItem'
import events from '../../../../events.json'

const SRCI_ENROLLMENT_ERROR_HANDLING_TIMEOUT = 1000 // 1s

/**
 * Launches DCF for a given network
 * @param {Object} data - postRobot payload of checkout data passed from Cersei
 */
export default async function launchDCF ({ data }) {
  let dcfResponse
  // Attempt to start and communicate with DCF
  try {
    const { network, ...result } = await communicateWithDCF(data)

    dcfResponse = result
  } catch (error) {
    // logging on lower env
    if (!state.isProduction && getLocalStorageItem('mcsrc.enablelogging')) {
      console.error('[CHECKOUT ERROR]', error)
    }
    // enrollment issue, will attempt to let SRCI do the error handling, and will abort checkout if it fails to respond
    scheduleAbortCheckoutOnSRCITimeout()
    throw error
  }

  // Handle Unbind Request (may happen in multiple scenarios)
  await routines.handleUnbindRequest(dcfResponse)

  // Proceed with requested action
  switch (dcfResponse.dcfActionCode) {
    case ACTIONS.COMPLETE:
      return completeCheckout(dcfResponse)
    case ACTIONS.SWITCH_CONSUMER:
      return reLaunchSRCi(dcfResponse, data.networkId, routines.onSwitchConsumer)
    case ACTIONS.CHANGE_CARD:
      return reLaunchSRCi(dcfResponse, data.networkId, routines.onChangeCard)
    case ACTIONS.AUTH_SKIPPED:
    case ACTIONS.AUTH_FAILED:
      return reLaunchSRCi(dcfResponse, data.networkId)
    case ACTIONS.CANCEL:
    case ACTIONS.ERROR:
      return abortCheckout()
    default:
      lumber.error({
        ...events.errors.INVALID_DCF_ACTION_CODE,
        details: {
          message: 'Invalid DCF action code received',
          network: data.networkId,
          action: dcfResponse.dcfActionCode
        }
      })
      return abortCheckout()
  }
}

/**
 * Starts the correct DCF instance using the sdk manager and waits for its completion
 * @param {object} data - entire checkout params payload received from SRCI
 * @param {Object} data.checkoutParams - EMVco standarized checkout parameters
 * @param {Object} data.sdkSpecificParameters - non-EMVco, network specific checkout parameters
 * @param {Enum<amex, visa, discover, mastercard>} data.networkId - NON-EMVco: network selected for checkout
 */
async function communicateWithDCF ({ networkId, checkoutParams, sdkSpecificParameters }) {
  // Launch DCF and wait for completion
  const dcfResponse = await state.aggregator.src.checkout({
    ...checkoutParams,
    ...(sdkSpecificParameters && sdkSpecificParameters[networkId]
      ? sdkSpecificParameters[networkId] : {}),
    networkId: networkId,
    windowRef: popupManager.getWindow()
  })

  if (!dcfResponse) {
    const error = new Error('Invalid DCF Response')

    lumber.error({
      ...events.errors.DCF_RESPONSE_ERROR,
      details: {
        network: networkId,
        message: `A checkout exception was thrown by the ${networkId} DCF to SRCi`,
        error: error
      }
    })

    throw error
  }

  return dcfResponse
}

/**
 * reLaunchSRCi with a different action then normal init
 * @description launches the SRCi when the user takes an action in the DCF
 * @param {objet} dcfResponse
 * @param {string} networkId - network name
 * @param {function} beforeLaunchingSRCI (optional)
 */
async function reLaunchSRCi (dcfResponse, networkId, beforeLaunchingSRCI) {
  // Save action for next SRCI_READY post-message
  state.srciActionCode = dcfResponse.dcfActionCode

  // Perform appropriate routine if necessary
  beforeLaunchingSRCI && await beforeLaunchingSRCI(dcfResponse, networkId)

  // Relaunch SRCI
  popupManager.getWindow().location.href = getSrciUrl()
}

/**
 * Schedules a check to ensure SRCI did handle the enrollment error properly.
 * Will abort checkout otherwise.
 * Note: There is no way for merchant js to ensure SRCI is still alive and
 * ready to handle it reliably on certain browsers, even with its window reference.
 * We will be waiting for a post-message instead.
 */
function scheduleAbortCheckoutOnSRCITimeout () {
  state.srciDidHandleEnrollmentError = false
  const interval = setInterval(() => {
    clearInterval(interval)
    if (!state.srciDidHandleEnrollmentError) {
      return abortCheckout()
    }
  }, SRCI_ENROLLMENT_ERROR_HANDLING_TIMEOUT)
}
